import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

// Display-only currency. AED is the official price and the Stripe charge currency.
export const CURRENCIES = ["AED", "USD", "EUR", "GBP", "SAR", "KWD", "QAR", "BHD", "OMR", "EGP", "JOD", "CAD", "AUD", "TRY", "INR"];
const KEY = "djspy_currency";
const Ctx = createContext(null);

const aedText = (n) => `AED ${Number(n || 0).toFixed(2)}`;

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try { return CURRENCIES.includes(localStorage.getItem(KEY)) ? localStorage.getItem(KEY) : "AED"; } catch { return "AED"; }
  });
  const [fx, setFx] = useState(null);

  // Rates are only fetched once a non-AED currency is in use (keeps AED visitors at zero requests).
  useEffect(() => {
    if (currency === "AED" || fx) return;
    api.get("/site/fx").then((r) => setFx(r.data)).catch(() => setFx({ rates: { AED: 1 }, stale: true }));
  }, [currency, fx]);

  const setCurrency = useCallback((c) => {
    if (!CURRENCIES.includes(c)) return;
    setCurrencyState(c);
    try { localStorage.setItem(KEY, c); } catch { /* private mode */ }
  }, []);

  const rate = currency === "AED" ? 1 : fx?.rates?.[currency];
  const active = currency !== "AED" && rate ? currency : "AED";

  const format = useCallback((aed) => {
    if (active === "AED") return aedText(aed);
    const v = Number(aed || 0) * rate;
    const s = new Intl.NumberFormat("en", { style: "currency", currency: active, currencyDisplay: "code" }).format(v);
    return `≈ ${s.replace(/\u00a0/g, " ")}`;
  }, [active, rate]);

  const value = useMemo(() => ({
    currency, active, setCurrency, format, aedText, converted: active !== "AED",
    fxSource: fx?.source || null, fxAttribution: fx?.attribution || null, fxFetchedAt: fx?.fetched_at || null,
    fxUnavailable: currency !== "AED" && fx && !rate,
  }), [currency, active, setCurrency, format, fx, rate]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCurrency() {
  return useContext(Ctx) || { currency: "AED", active: "AED", setCurrency: () => {}, format: aedText, aedText, converted: false };
}
